import { useContext, useCallback } from 'react'; // Added useCallback
import {
  getFirestore, doc, setDoc, getDoc, collection, query,
  Timestamp, deleteDoc, getDocs
} from 'firebase/firestore';
import * as localStorageService from '../services/localStorageService';
import type { BuildData } from '../types';
import { FirebaseContext } from '../contexts/firebaseContext';

interface CustomGlobal {
    __app_id?: string;
}

// --- GLOBAL APP ID ---
// NOTE: __app_id is provided by the hosting environment.
const appId = (globalThis as unknown as CustomGlobal).__app_id || 'default-sworn-builder';

export interface StorageHook {
  userId: string | null;
  isReady: boolean;
  useLocalStorage: boolean;
  saveBuild: (build: Omit<BuildData, 'id' | 'createdAt' | 'updatedAt'>) => Promise<BuildData>;
  getAllBuilds: () => Promise<BuildData[]>;
  deleteBuild: (id: string) => Promise<boolean>;
  updateBuild: (id: string, updates: Partial<Omit<BuildData, 'id' | 'createdAt'>>) => Promise<BuildData | null>; // Omit non-updatable fields
  shareBuild: (build: BuildData) => Promise<string | null>;
  getSharedBuild: (shareId: string) => Promise<BuildData | null>;
}

/**
 * Custom hook to manage build storage, automatically switching
 * between Firebase and localStorage based on the Firebase context.
 */
export const useStorage = (): StorageHook => {
  // Get storage mode and credentials from context
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useStorage must be used within a FirebaseProvider');
  }
  const { db, userId, isAuthReady, useLocalStorage } = context;

  const getBuildsCollection = useCallback(() => { // Wrap in useCallback
    if (!db || !userId) {
        throw new Error("Firebase is not initialized or user is not authenticated.");
    }
    // All builds are stored in a 'builds' subcollection under the user's private doc
    return collection(db, 'artifacts', appId, 'users', userId, 'builds');
  }, [db, userId]); // Add dependencies

  const saveBuild = useCallback(async (build: Omit<BuildData, 'id' | 'createdAt' | 'updatedAt'>): Promise<BuildData> => {
    // Fallback to localStorage if specified or if db/user is not ready
    if (useLocalStorage || !db || !userId) {
      // Wrap localStorage call in Promise.resolve for consistent async return
      return Promise.resolve(localStorageService.saveBuild(build));
    }

    // --- Firebase Logic ---
    const newBuild: BuildData = {
      ...build,
      id: crypto.randomUUID(), // Use UUID for Firebase doc ID
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const docRef = doc(getBuildsCollection(), newBuild.id);

    // Store with Firestore Timestamps
    await setDoc(docRef, {
      ...newBuild,
      createdAt: Timestamp.fromDate(new Date(newBuild.createdAt)),
      updatedAt: Timestamp.fromDate(new Date(newBuild.updatedAt))
    });

    return newBuild;
  }, [db, userId, useLocalStorage, getBuildsCollection]); // Add dependencies

  const getAllBuilds = useCallback(async (): Promise<BuildData[]> => {
    if (useLocalStorage || !db || !userId) {
      // Wrap localStorage call in Promise.resolve
      return Promise.resolve(localStorageService.getAllBuilds());
    }

    // --- Firebase Logic ---
    try {
      const buildsCol = getBuildsCollection();
      // NOTE: Removed orderBy('updatedAt', 'desc') to avoid needing a composite index.
      // We will sort the results in-memory.
      const q = query(buildsCol);
      const snapshot = await getDocs(q);

      const builds = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          ...data,
          id: docSnap.id,
          // Convert Timestamps back to ISO strings for consistency
          createdAt: (data.createdAt as Timestamp)?.toDate().toISOString() || new Date(0).toISOString(), // Add fallback
          updatedAt: (data.updatedAt as Timestamp)?.toDate().toISOString() || new Date(0).toISOString(), // Add fallback
        } as BuildData;
      });

      // Sort manually by date
      return builds.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

    } catch (e) {
      console.error("Error fetching builds from Firestore:", e);
      return [];
    }
  }, [db, userId, useLocalStorage, getBuildsCollection]); // Add dependencies

  const deleteBuild = useCallback(async (id: string): Promise<boolean> => {
    if (useLocalStorage || !db || !userId) {
      // Wrap localStorage call in Promise.resolve
      return Promise.resolve(localStorageService.deleteBuild(id));
    }

    // --- Firebase Logic ---
    try {
      const docRef = doc(getBuildsCollection(), id);
      await deleteDoc(docRef);
      return true;
    } catch (e) {
      console.error("Error deleting document:", e);
      return false;
    }
  }, [db, userId, useLocalStorage, getBuildsCollection]); // Add dependencies

  const updateBuild = useCallback(async (id: string, updates: Partial<Omit<BuildData, 'id' | 'createdAt'>>): Promise<BuildData | null> => {
    if (useLocalStorage || !db || !userId) {
      // Ensure updateBuild in localStorageService handles Partial correctly
       const updatedLocalBuild = localStorageService.updateBuild(id, updates);
       // Need to ensure the return type matches Promise<BuildData | null>
       return Promise.resolve(updatedLocalBuild);
    }

    // --- Firebase Logic ---
    try {
        const docRef = doc(getBuildsCollection(), id);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) return null;

        const updatedData = {
          ...updates,
          updatedAt: Timestamp.now() // Always update the timestamp
        };

        // Use setDoc with merge:true to update existing doc
        await setDoc(docRef, updatedData, { merge: true });

        // Fetch the updated doc to return the complete, merged data
        const updatedDocSnap = await getDoc(docRef);
        if (!updatedDocSnap.exists()) return null; // Should not happen, but good practice

        const finalData = updatedDocSnap.data();

        return {
            ...finalData,
            id: updatedDocSnap.id,
            createdAt: (finalData.createdAt as Timestamp)?.toDate().toISOString() || new Date(0).toISOString(), // Add fallback
            updatedAt: (finalData.updatedAt as Timestamp)?.toDate().toISOString() || new Date(0).toISOString(), // Add fallback
        } as BuildData;

    } catch (e) {
        console.error("Error updating document:", e);
        return null;
    }
  }, [db, userId, useLocalStorage, getBuildsCollection]); // Add dependencies

  const shareBuild = useCallback(async (build: BuildData): Promise<string | null> => {
    if (useLocalStorage || !db) {
      console.warn("Sharing is not supported in local mode.");
      return null;
    }

    try {
      // Create user-friendly ID: buildname_random5
      const sanitizedName = (build.name || 'build')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '') // Remove non-alphanumeric characters (spaces, special chars)
        .substring(0, 30); // Limit length
      
      const baseName = sanitizedName || 'build';
      
      let shareId = '';
      let isUnique = false;
      let attempts = 0;

      // Try to generate a unique ID
      while (!isUnique && attempts < 5) {
        const suffix = Math.random().toString(36).substring(2, 7); // 5 random chars
        shareId = `${baseName}_${suffix}`;
        
        const docRef = doc(db, 'artifacts', appId, 'shared_builds', shareId);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
          isUnique = true;
        }
        attempts++;
      }

      // Fallback to random UUID if collision check somehow fails consistently
      if (!isUnique) {
        shareId = crypto.randomUUID();
      }

      const sharedBuildRef = doc(db, 'artifacts', appId, 'shared_builds', shareId);

      await setDoc(sharedBuildRef, {
        ...build,
        sourceBuildId: build.id,
        sharedAt: Timestamp.now(),
        originalOwnerId: userId,
        isCommunityBuild: false,
        communityRank: 0,
        isSourceDeleted: false
      });

      return shareId;
    } catch (e) {
      console.error("Error sharing build:", e);
      return null;
    }
  }, [db, userId, useLocalStorage]);

  const getSharedBuild = useCallback(async (shareId: string): Promise<BuildData | null> => {
    if (!db) return null;

    try {
      const sharedBuildRef = doc(db, 'artifacts', appId, 'shared_builds', shareId);
      const docSnap = await getDoc(sharedBuildRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // Helper to safely convert Firestore timestamps or strings to ISO string
        const toIso = (val: unknown) => {
            if (val && typeof val === 'object' && 'toDate' in val && typeof (val as { toDate: () => Date }).toDate === 'function') return (val as { toDate: () => Date }).toDate().toISOString();
            if (typeof val === 'string') return val;
            return new Date().toISOString();
        };

        return {
          ...data,
          id: docSnap.id, 
          createdAt: toIso(data.createdAt),
          updatedAt: toIso(data.updatedAt),
          sharedAt: toIso(data.sharedAt),
        } as BuildData;
      }
      return null;
    } catch (e) {
      console.error("Error fetching shared build:", e);
      return null;
    }
  }, [db]);

  return {
    userId,
    isReady: isAuthReady,
    useLocalStorage,
    saveBuild,
    getAllBuilds,
    deleteBuild,
    updateBuild,
    shareBuild,
    getSharedBuild
  };
};
